import React, { useState, createContext, useContext, useEffect } from "react";

import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { createPortal } from "react-dom";

import {
  DEFAULT_TOAST_DURATION,
  normalizeToastContent,
  toastManager,
  toast,
  type ToastContent,
  type ToastType,
} from "../../Utils/Helper/toast";
import "./style.scss";

// Re-export toast utility for convenience (so users can import from either location)
export { toast };
export type { ToastContent, ToastType };

interface ToastItem {
  id: number;
  title: string;
  message: string;
  type: ToastType;
  duration: number;
}

const ToastContext = createContext<{
  success: (content: ToastContent, duration?: number) => number;
  error: (content: ToastContent, duration?: number) => number;
  warning: (content: ToastContent, duration?: number) => number;
  info: (content: ToastContent, duration?: number) => number;
  addToast: (
    content: ToastContent,
    type?: ToastType,
    duration?: number
  ) => number;
  removeToast: (id: number) => void;
} | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toastItem => toastItem.id !== id));
  };

  const addToast = (
    content: ToastContent,
    type: ToastType = "info",
    duration = DEFAULT_TOAST_DURATION
  ): number => {
    const id = Date.now() + Math.random();
    const { title, message } = normalizeToastContent(content, type);
    const toastItem: ToastItem = {
      id,
      title,
      message,
      type,
      duration,
    };

    setToasts(prev => [...prev, toastItem]);

    if (duration > 0) {
      window.setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  };

  const success = (content: ToastContent, duration?: number) =>
    addToast(content, "success", duration);
  const error = (content: ToastContent, duration?: number) =>
    addToast(content, "error", duration);
  const warning = (content: ToastContent, duration?: number) =>
    addToast(content, "warning", duration);
  const info = (content: ToastContent, duration?: number) =>
    addToast(content, "info", duration);

  useEffect(() => {
    toastManager.setToastFunctions({ success, error, warning, info });
  }, []);

  return (
    <ToastContext.Provider
      value={{ success, error, warning, info, addToast, removeToast }}
    >
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

/**
 * Where other always-on pop-ups join the column (Account Plans' plan pop-ups,
 * 2026-11-04): below the app's own toasts, so the two never overlap.
 */
export const TOAST_SLOT_ID = "toast-slot";

function ToastContainer({
  toasts,
  removeToast,
}: {
  toasts: ToastItem[];
  removeToast: (id: number) => void;
}) {
  const container = (
    <div className="toast-wrapper">
      {toasts.map(toastItem => (
        <Toast
          key={toastItem.id}
          toast={toastItem}
          onClose={() => removeToast(toastItem.id)}
        />
      ))}
      <div id={TOAST_SLOT_ID} className="toast-slot" />
    </div>
  );

  return createPortal(container, document.body);
}

export function ToastIcon({ type }: { type: ToastType }) {
  switch (type) {
    case "success":
      return <CheckCircle2 aria-hidden="true" />;
    case "error":
      return <XCircle aria-hidden="true" />;
    case "warning":
      return <AlertTriangle aria-hidden="true" />;
    case "info":
    default:
      return <Info aria-hidden="true" />;
  }
}

export interface ToastCardProps {
  type: ToastType;
  title: string;
  message?: string;
  /** Replaces the type's icon (e.g. a spinner while something is in progress). */
  icon?: React.ReactNode;
  /** One button under the message, for a pop-up that asks the reader to act. */
  action?: { label: string; onClick: () => void };
  onClose?: () => void;
  /** Milliseconds for the countdown bar; 0 or unset shows none. */
  duration?: number;
  /** One line, no message: a status that stays while something runs. */
  compact?: boolean;
  exiting?: boolean;
  className?: string;
  testId?: string;
  role?: "alert" | "status";
}

/** The toast's look on its own, for pop-ups whose life is managed elsewhere. */
export function ToastCard({
  type,
  title,
  message,
  icon,
  action,
  onClose,
  duration,
  compact,
  exiting,
  className,
  testId,
  role = "alert",
}: ToastCardProps) {
  const toastClass = [
    "custom-toast",
    `custom-toast--${type}`,
    exiting ? "custom-toast--exiting" : "custom-toast--entering",
    compact ? "custom-toast--compact" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={toastClass}
      style={
        duration
          ? ({ "--toast-duration": `${duration}ms` } as React.CSSProperties)
          : undefined
      }
      role={role}
      aria-live="polite"
      data-testid={testId}
    >
      <div className="toast-accent" aria-hidden="true" />

      <div className="toast-body">
        <div className="toast-icon-wrap">
          {icon ?? <ToastIcon type={type} />}
        </div>

        <div className="toast-content">
          <p className="toast-title">{title}</p>
          {message && <p className="toast-message">{message}</p>}
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className="toast-action"
            >
              {action.label}
            </button>
          )}
        </div>

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="toast-close"
            aria-label="Close notification"
          >
            <X aria-hidden="true" />
          </button>
        )}
      </div>

      {!!duration && duration > 0 && (
        <div className="toast-progress-track" aria-hidden="true">
          <div className="toast-progress-bar" />
        </div>
      )}
    </div>
  );
}

function Toast({
  toast: toastItem,
  onClose,
}: {
  toast: ToastItem;
  onClose: () => void;
}) {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    window.setTimeout(onClose, 300);
  };

  return (
    <ToastCard
      type={toastItem.type}
      title={toastItem.title}
      message={toastItem.message}
      duration={toastItem.duration}
      exiting={isExiting}
      onClose={handleClose}
    />
  );
}
